import { X } from "lucide-react";

import { ReactComponent as EyeIcon } from "@assets/svg/blue-eye.svg";
import { ReactComponent as FileIcon } from "@assets/svg/document-filled.svg";

import { Button } from "@components/ui/button";

type FileItemProps = {
  /** The file object being displayed */
  file: File;
  /** Index of the file in the list */
  index: number;
  /** Current upload/processing status of the file */
  status: "pending" | "uploading" | "success" | "error";
  /** Error message if the file validation or upload failed */
  error?: string;
  /** Callback when preview button is clicked */
  onPreview?: (file: File, index: number) => void;
  /** Callback when remove button is clicked */
  onRemove: (file: File, index: number) => void;
  /** Function to format file size for display */
  formatFileSize: (bytes: number) => string;
};

/**
 * FileItem component
 *
 * Displays a single file with its name, size, and action buttons.
 * Shows file icon, preview button (if onPreview provided), and remove button.
 * Displays error message if file validation or upload failed.
 *
 * @param file - The file object being displayed
 * @param index - Index of the file in the list
 * @param status - Current upload/processing status of the file
 * @param error - Error message if the file validation or upload failed
 * @param onPreview - Callback when preview button is clicked
 * @param onRemove - Callback when remove button is clicked
 * @param formatFileSize - Function to format file size for display
 */
export const FileItem = ({
  file,
  index,
  status,
  error,
  onPreview,
  onRemove,
  formatFileSize,
}: FileItemProps) => {
  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2 rounded-xl bg-background-white p-3">
      <div className="grid grid-cols-[auto_1fr] items-center gap-3">
        <figure className="shrink-0 rounded-full bg-accent-50 p-2">
          <FileIcon className="size-5" />
        </figure>

        <div className="min-w-0 overflow-hidden">
          <p className="truncate text-sm font-bold">{file.name}</p>
          <p className="text-xs text-foreground-muted">
            {formatFileSize(file.size)}
          </p>
          {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
        </div>
      </div>

      {/* Preview button */}
      {onPreview && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="p-2 text-primary"
          onClick={() => onPreview(file, index)}
          disabled={status === "uploading"}
        >
          <EyeIcon className="size-4" />
          Preview
        </Button>
      )}

      {/* Remove button */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="p-2"
        onClick={() => onRemove(file, index)}
        disabled={status === "uploading"}
      >
        <X className="size-4" />
      </Button>
    </div>
  );
};
